import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { queryKeys } from "@/lib/query-keys";
import type { AnalyticsReportParams } from "./useAnalyticsReport";

interface DashboardViewState {
  presetDays: number | null;
  dateLabel: string;
  params: AnalyticsReportParams;
  breakdown: string;
}

/** Resolves a rolling preset against now; custom calendar bounds must bypass this helper. */
export function rollingRange(days: number) {
  const to = new Date();
  return {
    from: new Date(to.getTime() - days * 86400000).toISOString(),
    to: to.toISOString(),
  };
}

function initialView(): DashboardViewState {
  return {
    presetDays: 7,
    dateLabel: "Past 7 days",
    breakdown: "endpoints",
    params: {
      ...rollingRange(7),
      granularity: "day",
      status: "all",
      requestId: "",
      page: 1,
    },
  };
}

/** Retains organization-scoped selections for 30 minutes in memory. Rolling presets advance on mount; custom bounds stay fixed. Mount the consumer keyed by organization ID. */
export function useAnalyticsViewState(orgId: string) {
  const client = useQueryClient();
  const { data: cachedView } = useQuery({
    queryKey: queryKeys.analytics.view(orgId),
    queryFn: initialView,
    initialData: initialView,
    enabled: false,
    staleTime: Infinity,
    gcTime: 30 * 60_000,
  });
  const [view, setView] = useState(() =>
    cachedView.presetDays === null
      ? cachedView
      : {
          ...cachedView,
          params: {
            ...cachedView.params,
            ...rollingRange(cachedView.presetDays),
          },
        },
  );
  useEffect(() => {
    client.setQueryData(queryKeys.analytics.view(orgId), view);
  }, [client, orgId, view]);
  return [view, setView] as const;
}
